import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";

import { getInAppAgentProfile } from "@/services/api/in-app-agent";
import { setFeOpenAppAllowlist, setFeToolAllowlist } from "@/services/agent";
import { useAni, type AniPersonalityMode } from "@/context/AniContext";
import { useFloatingAniProfileCompanyId } from "@/hooks/useFloatingAniProfileCompanyId";
import { useIsAdmin } from "@/hooks/useIsAdmin";

const PERSONALITY_MODE_STORAGE_KEY = "ani_personality_mode";

function isAniPersonalityMode(value: unknown): value is AniPersonalityMode {
  return value === "professional" || value === "casual" || value === "fun";
}

function parseAppSamplePrompts(raw: unknown): Record<string, string[]> | undefined {
  if (!raw || typeof raw !== "object") {
    return undefined;
  }
  const out: Record<string, string[]> = {};
  for (const [appId, rows] of Object.entries(raw as Record<string, unknown>)) {
    if (!Array.isArray(rows)) {
      continue;
    }
    const prompts = rows.filter((row): row is string => typeof row === "string" && row.trim().length > 0);
    if (prompts.length > 0) {
      out[appId] = prompts;
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export function useInAppAgentProfile(companyId: string | undefined) {
  const { setInitialAssistantMessage, setSamplePrompts, setAppSamplePrompts, setPersonalityMode } = useAni();
  const appliedCompanyRef = useRef<string | undefined>(undefined);
  const isAdmin = useIsAdmin();
  const canOverrideProfileCompany = companyId === "ani" && isAdmin;
  const { profileCompanyId: profileOverride } = useFloatingAniProfileCompanyId(canOverrideProfileCompany);
  const appliedProfileKey = profileOverride || companyId;

  const query = useQuery({
    queryKey: ["in-app-agent-profile", companyId, profileOverride],
    queryFn: () => getInAppAgentProfile(profileOverride || undefined),
    enabled: Boolean(companyId),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!companyId) {
      setFeOpenAppAllowlist(undefined);
      setFeToolAllowlist(undefined);
      setInitialAssistantMessage(undefined);
      setSamplePrompts(undefined);
      setAppSamplePrompts(undefined);
      return;
    }

    // Same-tenant refetch: keep allowlists while loading. Company switch: deny until new profile loads.
    if (query.isLoading) {
      if (appliedCompanyRef.current !== appliedProfileKey) {
        setFeOpenAppAllowlist([]);
        setFeToolAllowlist([]);
      }
      return;
    }

    const profile = query.data?.profile;
    if (!profile) {
      setFeOpenAppAllowlist([]);
      setFeToolAllowlist([]);
      setInitialAssistantMessage(undefined);
      setSamplePrompts(undefined);
      setAppSamplePrompts(undefined);
      return;
    }

    const rawAppIds = profile.fe_open_app_ids;
    const appIds =
      Array.isArray(rawAppIds) && rawAppIds.length > 0
        ? rawAppIds.filter((row): row is string => typeof row === "string")
        : undefined;
    setFeOpenAppAllowlist(appIds);

    const feTools = Array.isArray(profile.fe_tool_allowlist)
      ? profile.fe_tool_allowlist.filter((row): row is string => typeof row === "string")
      : undefined;
    setFeToolAllowlist(feTools);

    if (typeof profile.initial_assistant_message === "string") {
      setInitialAssistantMessage(profile.initial_assistant_message);
    } else {
      setInitialAssistantMessage(undefined);
    }

    if (Array.isArray(profile.sample_prompts)) {
      setSamplePrompts(profile.sample_prompts.filter((row): row is string => typeof row === "string"));
    } else {
      setSamplePrompts(undefined);
    }

    setAppSamplePrompts(parseAppSamplePrompts(profile.app_sample_prompts));

    const defaultMode = profile.default_personality_mode;
    if (isAniPersonalityMode(defaultMode)) {
      const stored = localStorage.getItem(PERSONALITY_MODE_STORAGE_KEY);
      if (!isAniPersonalityMode(stored)) {
        setPersonalityMode(defaultMode);
      }
    }

    appliedCompanyRef.current = appliedProfileKey;
  }, [query.data?.profile, query.isLoading, companyId, appliedProfileKey, setInitialAssistantMessage, setSamplePrompts, setAppSamplePrompts, setPersonalityMode]);

  return query;
}
